import clsx from "clsx";
import { KTSVG } from "../../../../../../resources/helpers/components/KTSVG";
import moment from "moment";
import { useMediaQuery } from "react-responsive";
import { t } from "i18next";
import { format as dateFormat } from "date-fns"; // Import date-fns format directly

// Properly implement the formatComplexDate function with return type
const formatComplexDate = (dateValue: any, formatString: string = ""): string => {
  try {
    // Check if we have a value
    if (!dateValue) return " - ";

    let date: Date;

    // Handle complex date object format
    if (typeof dateValue === "object" && dateValue !== null) {
      // Case 1: Complex object with _type field
      if (dateValue._type) {
        // Handle date type (used by orderTime)
        if (dateValue._type === "date") {
          if (dateValue.iso) {
            date = new Date(dateValue.iso);
          } else if (dateValue.timestamp) {
            date = new Date(dateValue.timestamp);
          } else {
            date = new Date(dateValue);
          }
        }
        // Handle timestamp type (used by shipByDate)
        else if (dateValue._type === "timestamp") {
          if (dateValue.iso) {
            date = new Date(dateValue.iso);
          } else if (dateValue.seconds !== undefined) {
            date = new Date(dateValue.seconds * 1000);
          } else {
            date = new Date(dateValue);
          }
        } else {
          date = new Date(dateValue);
        }
      }
      // Case 2: Firebase timestamp (with toDate method)
      else if (typeof dateValue.toDate === "function") {
        date = dateValue.toDate();
      }
      // Case 3: Firebase timestamp with seconds
      else if (dateValue.seconds !== undefined) {
        date = new Date(dateValue.seconds * 1000);
      }
      // Case 4: Regular object, try direct conversion
      else {
        date = new Date(dateValue);
      }
    }
    // Case 5: Simple string or number
    else {
      date = new Date(dateValue);
    }

    // Verify the date is valid
    if (isNaN(date.getTime())) {
      console.warn("Invalid date value:", dateValue);
      return " - ";
    }

    // Format the date
    return dateFormat(date, formatString);
  } catch (error) {
    console.error("Error formatting date:", error, dateValue);
    return " - ";
  }
};

interface IShippingLimitLabelProps {
  shippingTimestamp?: any;
}

const getShippingLabelName = (shippingLimit: number) => {
  if (shippingLimit === 0) {
    return t("Common.Pending");
  } else if (shippingLimit <= 0) {
    return t("Common.Expired");
  } else {
    return `${shippingLimit} ${t("Common.ShortHour")}`;
  }
};

const ShippingLimitLabel: React.FC<IShippingLimitLabelProps> = ({
  shippingTimestamp,
}: IShippingLimitLabelProps) => {
  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 900px)" });

  // Convert complex timestamp to moment object
  let shipDate;

  if (shippingTimestamp) {
    try {
      // Use our formatter to get properly formatted date string
      const dateStr = formatComplexDate(
        shippingTimestamp,
        "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'"
      );
      if (dateStr && dateStr !== " - ") {
        shipDate = moment(dateStr);
      }
    } catch (error) {
      console.error("Error parsing timestamp:", error);
      shipDate = null;
    }
  }

  const nowDate = moment();
  const shippingLimit = shipDate?.diff(nowDate, "hours") || 0;
  const shippingLimitLabel = getShippingLabelName(shippingLimit);

  return (
    <div
      style={{ width: isTabletOrMobile ? "70px" : "100px" }}
      className={
        !isTabletOrMobile
          ? `p-2 rounded-5 text-center fw-bold d-flex justify-content-start
        ${clsx({
          "btn btn-sm btn-light-danger alert-danger":
            shippingLimit <= 4 && shippingLimit > 0,
          "btn btn-sm btn-light-success alert-success": shippingLimit > 4,
          "btn btn-sm btn-light-warning alert-warning": shippingLimit < 0,
          "btn btn-sm btn-secondary alert-secondary": shippingLimit === 0,
        })}    
        }` // Desktop Display
          : `p-1 rounded-5 text-center fw-bold d-flex justify-content-end m-0
        ${clsx({
          "btn btn-sm btn-light-danger alert-danger":
            shippingLimit <= 4 && shippingLimit > 0,
          "btn btn-sm btn-light-success alert-success": shippingLimit > 4,
          "btn btn-sm btn-light-warning alert-warning": shippingLimit < 0,
          "btn btn-sm btn-light-secondary alert-secondary": shippingLimit === 0,
        })}    
        }` // Mobile Display
      }
    >
      <KTSVG
        path="/media/icons/duotune/general/gen013.svg"
        className={`p-0 m-0 ${clsx({
          "svg-icon-danger": shippingLimit <= 4 && shippingLimit > 0,
          "svg-icon-success": shippingLimit > 4,
          "svg-icon-warning": shippingLimit < 0,
          "svg-icon-secondary": shippingLimit === 0,
        })} my-auto`}
      />
      <div className="px-1 mx-0">
        <span className="p-0 m-0">{shippingLimitLabel}</span>
      </div>
    </div>
  );
};

export default ShippingLimitLabel;
